

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Tabs,
  Tab,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Paper,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
  Switch,
  Divider
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import { useAuth } from 'auth/Auth';

// Define available fields for Lead and Case
const LEAD_FIELDS = [
  { id: 'Salutation', label: 'Salutation', required: false },
  { id: 'first_name', label: 'First Name', required: false },
  { id: 'last_name', label: 'Last Name', required: true },
  { id: 'email', label: 'Email', required: true },
  { id: 'company_name', label: 'Company', required: false },
  { id: 'phone', label: 'Phone', required: false },
  { id: 'mobile', label: 'Mobile', required: false },
  { id: 'industry', label: 'Industry', required: false },
  { id: 'notes', label: 'Comment', required: false },
  { id: 'website', label: 'Website', required: false },
  { id: 'lead_status', label: 'Status', required: false },
];

const CASE_FIELDS = [
  { id: 'subject', label: 'Subject', required: true },
  { id: 'description', label: 'Description', required: true },
  { id: 'first_name', label: 'Contact First Name', required: false },
  { id: 'last_name', label: 'Contact Last Name', required: true },
  { id: 'phone', label: 'Contact Phone', required: false },
  { id: 'email', label: 'Contact Email', required: false },
  // { id: 'priority', label: 'Priority', required: false },
  // { id: 'type', label: 'Case Type', required: false },
  { id: 'status', label: 'Status', required: false },
  // { id: 'case_source', label: 'Case Source', required: false },
  // { id: 'category', label: 'Category', required: false },
];

function WebFormsSettings() {
  const { axiosInstance, user, token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedLeadFields, setSelectedLeadFields] = useState(
    LEAD_FIELDS.filter(field => field.required).map(field => field.id)
  );
  const [selectedCaseFields, setSelectedCaseFields] = useState(
    CASE_FIELDS.filter(field => field.required).map(field => field.id)
  );
  const [redirectUrl, setRedirectUrl] = useState('');
  const [formTitle, setFormTitle] = useState('Contact Us');
  const [submitButtonText, setSubmitButtonText] = useState('Submit');
  const [successMessage, setSuccessMessage] = useState('Thank you for your submission!');
  const [useCustomStyles, setUseCustomStyles] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#1565C8');
  const [generatedCode, setGeneratedCode] = useState('');
  const [schemaName, setSchemaName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [websiteDomain, setWebsiteDomain] = useState('');

  // Add debug log when component mounts
  useEffect(() => {
    console.log('Auth state:', {
      user: !!user,
      token: !!token,
      tokenValue: token?.substring(0, 10) + '...'
    });
  }, [user, token]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setGeneratedCode('');
  };

  const handleFieldToggle = (fieldId, formType) => {
    if (formType === 'lead') {
      setSelectedLeadFields(prev => {
        if (prev.includes(fieldId)) {
          return prev.filter(id => id !== fieldId);
        }
        return [...prev, fieldId];
      });
    } else {
      setSelectedCaseFields(prev => {
        if (prev.includes(fieldId)) {
          return prev.filter(id => id !== fieldId);
        }
        return [...prev, fieldId];
      });
    }
  };

  // Update the generateToken function
  const generateToken = async () => {
    console.log('Starting generateToken...');
    setIsLoading(true);
    setError('');

    try {
      // Validate inputs
      if (!websiteDomain || !token || !user?.id) {
        const errors = [];
        if (!websiteDomain) errors.push('Website domain is required');
        if (!token) errors.push('Authentication token is missing');
        if (!user?.id) errors.push('User ID is missing');
        throw new Error(errors.join('. '));
      }

      // const baseUrl = process.env.REACT_APP_API_URL;
      // const baseUrl = "http://localhost:8000";
      const baseUrl = process.env.REACT_APP_API_URL;
      const url = `${baseUrl}/external/generate-site-key/`; // Note the trailing slash

      // Prepare request body
      const params = new URLSearchParams({
        // default_user: user.id,
        domain: websiteDomain.toLowerCase().trim()
      }).toString();

      const requestUrl = `${url}?${params}`;
      console.log('Making API request:', {
        url: requestUrl,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token.substring(0, 10)}...`,
        }
      });

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('API Error:', {
          status: response.status,
          statusText: response.statusText,
          url: requestUrl,
          errorData
        });

        throw new Error(
          errorData?.detail ||
          `API request failed (${response.status}). Please check your configuration.`
        );
      }

      const data = await response.json();
      console.log('API Response:', {
        success: true,
        hasToken: !!data.token,
        hasSchemaName: !!data.schema_name,
        domain: data.domain
      });

      if (!data.token || !data.schema_name) {
        throw new Error('Invalid response from server - missing token or schema name');
      }

      setSchemaName(data.schema_name);
      return data.token;

    } catch (error) {
      console.error('Token generation failed:', {
        message: error.message,
        stack: error.stack
      });
      setError(`Failed to generate API token: ${error.message}`);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const generateFormCode = async (formType) => {
    console.log('Starting generateFormCode...', { formType });
    setIsLoading(true);
    setError('');

    try {
      console.log('Calling generateToken...');
      const token = await generateToken();
      console.log('Token received:', token ? 'yes' : 'no');

      if (!token) {
        console.log('No token received');
        setError('Failed to generate site key. Please try again.');
        return;
      }

      const fields = formType === 'lead' ? selectedLeadFields : selectedCaseFields;
      const allFields = formType === 'lead' ? LEAD_FIELDS : CASE_FIELDS;

      // Create form fields HTML
      let formFieldsHtml = '';
      allFields.forEach(field => {
        if (fields.includes(field.id)) {
          const isRequired = field.required;
          const inputType = field.id === 'email' ? 'email' :
            (field.id === 'description' ? 'textarea' : 'text');

          formFieldsHtml += `
            <div class="form-group">
                <label for="${field.id}">${field.label}${isRequired ? ' *' : ''}</label>
                ${inputType === 'textarea'
              ? `<textarea id="${field.id}" name="${field.id}" rows="4"${isRequired ? ' required' : ''}></textarea>`
              : `<input type="${inputType}" id="${field.id}" name="${field.id}"${isRequired ? ' required' : ''}>`
            }
            </div>`;
        }
      });

      // Add hidden fields
      const hiddenFields = formType === 'lead'
        ? `<input type="hidden" name="lead_source" value="Website">`
        : `<input type="hidden" name="case_source" value="Website">`;
      const externalUrl = process.env.REACT_APP_EXTERNAL_URL;
      // Create the complete form HTML with improved error handling and CORS support
      const formHtml = `
<!DOCTYPE html>
<html>
<head>
    <title>${formTitle} - ${formType.charAt(0).toUpperCase() + formType.slice(1)} Form</title>
    <style>
        .PearlCrm-form-container {
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            font-family: Arial, sans-serif;
            box-sizing: border-box;
        }
        .PearlCrm-form-group {
            margin-bottom: 15px;
        }
        .PearlCrm-form-container label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
        }
        .PearlCrm-form-container input[type="text"],
        .PearlCrm-form-container input[type="email"],
        .PearlCrm-form-container textarea,
        .PearlCrm-form-container select {
            width: 100%;
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            box-sizing: border-box;
        }
        .PearlCrm-form-container button {
            background-color: ${primaryColor};
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
        }
        .PearlCrm-form-container button:hover {
            background-color: ${primaryColor}dd;
        }
        .PearlCrm-error-message {
            color: #f44336;
            margin-top: 20px;
            padding: 10px;
            background-color: #ffebee;
            border-radius: 4px;
            display: none;
        }
        .PearlCrm-success-message {
            color: #4caf50;
            margin-top: 20px;
            padding: 10px;
            background-color: #e8f5e9;
            border-radius: 4px;
            display: none;
        }
        .PearlCrm-loading {
            display: none;
            text-align: center;
            margin-top: 20px;
        }
        .PearlCrm-spinner {
            border: 4px solid rgba(0, 0, 0, 0.1);
            width: 36px;
            height: 36px;
            border-radius: 50%;
            border-left-color: ${primaryColor};
            animation: PearlCrm-spin 1s linear infinite;
            margin: 0 auto;
        }
        @keyframes PearlCrm-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        ${useCustomStyles ? '/* Add your custom CSS here */' : ''}
    </style>
</head>
<body>
    <div class="PearlCrm-form-container">
        <h2>${formTitle}</h2>
        <form id="PearlCrm-web-form">
            ${formFieldsHtml}
            ${hiddenFields}
            <input type="hidden" name="PearlCrm_token" value="${token}">
            ${redirectUrl ? `<input type="hidden" name="redirect_url" value="${redirectUrl}">` : ''}
            <button type="submit" id="PearlCrm-submit-button">${submitButtonText}</button>
        </form>
        <div id="PearlCrm-loading" class="PearlCrm-loading">
            <div class="PearlCrm-spinner"></div>
            <p>Submitting your information...</p>
        </div>
        <div id="PearlCrm-error-message" class="PearlCrm-error-message">
            An error occurred. Please try again later.
        </div>
        <div id="PearlCrm-success-message" class="PearlCrm-success-message">
            ${successMessage}
        </div>
    </div>

    <script>
        // Define your API endpoint - this must be an absolute URL to your API server
        const PearlCrm_API_ENDPOINT = "${externalUrl}/external/${formType === 'lead' ? 'leads' : 'cases'}/";
        // const PearlCrm_API_ENDPOINT = "http://localhost:8000/external/${formType === 'lead' ? 'leads' : 'cases'}/";
        const PearlCrm_API_KEY = "${token}";
        
        document.getElementById('PearlCrm-web-form').addEventListener('submit', function(e) {
            e.preventDefault();
            
            // Show loading indicator
            document.getElementById('PearlCrm-loading').style.display = 'block';
            document.getElementById('PearlCrm-submit-button').disabled = true;
            document.getElementById('PearlCrm-error-message').style.display = 'none';
            document.getElementById('PearlCrm-success-message').style.display = 'none';
            
            const form = e.target;
            const formData = {};
            
            // Collect form data
            for (let i = 0; i < form.elements.length; i++) {
                const element = form.elements[i];
                if (element.name && element.name !== 'submit' && element.type !== 'submit' && element.name !== 'PearlCrm_token') {
                    formData[element.name] = element.value;
                }
            }
            
            // Use XMLHttpRequest instead of fetch for better compatibility
            const xhr = new XMLHttpRequest();
            xhr.open('POST', PearlCrm_API_ENDPOINT, true);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.withCredentials = false; // Important for CORS
            
            xhr.onload = function() {
                document.getElementById('PearlCrm-loading').style.display = 'none';
                document.getElementById('PearlCrm-submit-button').disabled = false;
                
                if (xhr.status >= 200 && xhr.status < 300) {
                    // Success
                    document.getElementById('PearlCrm-success-message').style.display = 'block';
                    document.getElementById('PearlCrm-error-message').style.display = 'none';
                    form.reset();
                    
                    // Redirect if URL is provided
                    ${redirectUrl ? `setTimeout(() => { window.location.href = "${redirectUrl}"; }, 2000);` : ''}
                } else {
                    // Error
                    let errorMsg = 'An error occurred. Please try again later.';
                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (response.detail) {
                            errorMsg = response.detail;
                        }
                    } catch (e) {
                        const text = xhr.responseText;
                        errorMsg = text.length > 100 ? text.substring(0, 100) + '...' : text;
                    }
                    
                    document.getElementById('PearlCrm-error-message').textContent = errorMsg;
                    document.getElementById('PearlCrm-error-message').style.display = 'block';
                    document.getElementById('PearlCrm-success-message').style.display = 'none';
                }
            };
            
            xhr.onerror = function() {
                document.getElementById('PearlCrm-loading').style.display = 'none';
                document.getElementById('PearlCrm-submit-button').disabled = false;
                document.getElementById('PearlCrm-error-message').textContent = 'Network error occurred. Please check your connection.';
                document.getElementById('PearlCrm-error-message').style.display = 'block';
                document.getElementById('PearlCrm-success-message').style.display = 'none';
            };
            
            // Include site_key in the form data
            formData.site_key = PearlCrm_API_KEY;
            
            xhr.send(JSON.stringify(formData));
        });
    </script>
</body>
</html>`;

      setGeneratedCode(formHtml);
    } catch (error) {
      console.error('Error generating form code:', error);
      setError('Failed to generate form code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedCode) {
      navigator.clipboard.writeText(generatedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <MDBox>
      <MDTypography className="pearl-dialog-dup-title" variant="h5" fontWeight="medium" mb={2}>
        Web Forms Configuration
      </MDTypography>

      <MDTypography variant="body2" color="text" mb={3}>
        Generate HTML forms for your website to capture leads and support cases directly into your CRM.
      </MDTypography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Web-to-Lead" />
        <Tab label="Web-to-Case" />
      </Tabs>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MDBox mb={3}>
            <MDTypography variant="h6" gutterBottom>
              Form Settings
            </MDTypography>

            <TextField
              fullWidth
              required
              label="Website Domain"
              value={websiteDomain}
              onChange={(e) => setWebsiteDomain(e.target.value)}
              placeholder="example.com"
              sx={{ mb: 2 }}
              helperText="Enter the domain where this form will be hosted (e.g. example.com)"
              error={!websiteDomain && error}
            />

            <TextField
              fullWidth
              label="Form Title"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Submit Button Text"
              value={submitButtonText}
              onChange={(e) => setSubmitButtonText(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Success Message"
              value={successMessage}
              onChange={(e) => setSuccessMessage(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Success Redirect URL (Optional)"
              value={redirectUrl}
              onChange={(e) => setRedirectUrl(e.target.value)}
              placeholder="https://example.com/thank-you"
              sx={{ mb: 2 }}
            />

            <MDBox display="flex" alignItems="center" mb={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomStyles}
                    onChange={(e) => setUseCustomStyles(e.target.checked)}
                    color="primary"
                  />
                }
                label="Use Custom Styling"
              />
              <Tooltip title="Enable to customize form appearance">
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MDBox>

            {useCustomStyles && (
              <TextField
                fullWidth
                label="Primary Color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                placeholder="#1565C8"
                sx={{ mb: 2 }}
                helperText="Color code for buttons and accents (e.g. #1565C8)"
              />
            )}
          </MDBox>

          <Divider sx={{ my: 3 }} />

          <MDTypography variant="h6" gutterBottom>
            Select Form Fields
          </MDTypography>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormGroup>
              {(activeTab === 0 ? LEAD_FIELDS : CASE_FIELDS).map((field) => (
                <FormControlLabel
                  key={field.id}
                  control={
                    <Checkbox
                      checked={activeTab === 0
                        ? selectedLeadFields.includes(field.id)
                        : selectedCaseFields.includes(field.id)
                      }
                      onChange={() => handleFieldToggle(field.id, activeTab === 0 ? 'lead' : 'case')}
                      disabled={field.required}
                    />
                  }
                  label={`${field.label}${field.required ? ' *' : ''}`}
                />
              ))}
            </FormGroup>
          </FormControl>

          <MDBox mt={3}>
            <MDButton
              color="info"
              onClick={() => {
                console.log('Generate Form Code button clicked');
                generateFormCode(activeTab === 0 ? 'lead' : 'case');
              }}
              disabled={isLoading}
              sx={{ position: 'relative' }}
            >
              {isLoading ? (
                <>
                  Generating...
                  <CircularProgress
                    size={24}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                </>
              ) : (
                'Generate Form Code'
              )}
            </MDButton>
          </MDBox>
        </Grid>

        <Grid item xs={12} md={6}>
          {generatedCode && (
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">
                  Generated Form Code
                </MDTypography>
                <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                  <IconButton onClick={copyToClipboard} color="info">
                    {copied ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </MDBox>
              <Paper
                sx={{
                  p: 2,
                  maxHeight: '500px',
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5'
                }}
              >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {generatedCode}
                </pre>
              </Paper>

              <MDBox mt={2}>
                <Alert severity="info">
                  This HTML form can be embedded on your website to capture {activeTab === 0 ? 'leads' : 'cases'} directly into your CRM.
                  The form includes validation and uses a secure authentication token.
                </Alert>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Implementation:</strong> Copy the entire HTML code and paste it into your website.
                  The form is self-contained with all necessary styling and functionality.
                </Alert>
              </MDBox>
            </MDBox>
          )}
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default WebFormsSettings; 